/**
 * Widget feed A/B tests, the widget's half.
 *
 * When next-sellwild runs an experiment on a feed, the listing-cache Lambda
 * lists its arms in the feed file's config ({ experiment, arms: [{ id,
 * buckets }] }) and writes one more file per arm, `<feed url>--<arm>`. The
 * reader's stored id (`_sw_uid`, the one the events queue uses) hashes to one
 * of ten tenths; a reader in an arm's tenths reads that arm's file, everyone
 * else stays on the control file. Clicks through to sellwild.com then carry
 * `swx=<experiment>&swr=<arm|control>`, which is how next-sellwild counts.
 *
 * The hash is FNV-1a, the same as next-sellwild's utils/ranking-experiments.ts.
 */

export const FEED_TENTHS = 10
export const FEED_CONTROL = 'control'

export interface FeedArmConfig {
  experiment?: string
  arms?: Array<{ id: string, buckets: number[] }>
}

/** What a reader got: the experiment and the arm (or control), for the click tag. */
export interface FeedArmPick {
  experiment: string
  arm: string
}

/** FNV-1a, 32-bit. */
export function hashKey (key: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i)
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return hash >>> 0
}

/** The tenth a reader id falls in, or null without an id (then the reader stays on control). */
export function tenthFor (readerId: string | null | undefined): number | null {
  const id = (readerId || '').trim()
  return id ? hashKey(id) % FEED_TENTHS : null
}

const ARM_ID = /^[a-z0-9][a-z0-9-]{0,23}$/

/**
 * The arm this reader gets, or null when the feed runs no experiment. A
 * reader in no arm's tenths, or with no id, is control. A malformed arm id is
 * ignored, since it ends up in a URL.
 */
export function pickFeedArm (config: FeedArmConfig | null | undefined, readerId: string | null | undefined): FeedArmPick | null {
  if (!config || !config.experiment || !Array.isArray(config.arms) || config.arms.length === 0) return null
  const tenth = tenthFor(readerId)
  const arm = tenth === null ? undefined : config.arms.find((a) => a && ARM_ID.test(a.id) && Array.isArray(a.buckets) && a.buckets.includes(tenth))
  return { experiment: config.experiment, arm: arm ? arm.id : FEED_CONTROL }
}

/** The arm's file next to the feed's: `<url>--<arm>`, keeping any query string. */
export function armFeedUrl (feedUrl: string, arm: string): string {
  const q = feedUrl.indexOf('?')
  return q < 0 ? `${feedUrl}--${arm}` : `${feedUrl.slice(0, q)}--${arm}${feedUrl.slice(q)}`
}

/** The click tag for a listing URL's query string: '' when the feed runs no experiment. */
export function armQuery (config: { swx?: string, swr?: string } | null | undefined): string {
  return config && config.swx && config.swr ? `&swx=${encodeURIComponent(config.swx)}&swr=${encodeURIComponent(config.swr)}` : ''
}

/** A feed file as the widget reads it. */
export interface FeedFile<L = unknown> {
  result: { rs: L[], config?: Record<string, unknown>, widgetCacheVersionId?: string, [k: string]: unknown }
  [k: string]: unknown
}

/**
 * The feed this reader should see, given the control file already loaded.
 * In an arm's tenths it fetches `<url>--<arm>`; a failed fetch keeps the
 * control file and tags the reader as control, so a missing arm file costs
 * nothing but that reader's place in the arm. The config always carries the
 * click tag (`swx`, `swr`) while an experiment runs.
 */
export async function withFeedArm<L> (
  url: string,
  control: FeedFile<L>,
  readerId: string | null | undefined,
  fetchJson: (url: string) => Promise<FeedFile<L>>,
): Promise<FeedFile<L>> {
  const config = control && control.result ? control.result.config : undefined
  const pick = pickFeedArm(config as FeedArmConfig | undefined, readerId)
  if (!pick) return control
  let used = control
  let arm = pick.arm
  if (arm !== FEED_CONTROL) {
    try {
      const file = await fetchJson(armFeedUrl(url, arm))
      if (!file || !file.result || !Array.isArray(file.result.rs) || file.result.rs.length === 0) throw new Error('empty arm file')
      used = file
    } catch {
      arm = FEED_CONTROL
    }
  }
  return { ...used, result: { ...used.result, config: { ...(config || {}), ...(used.result.config || {}), swx: pick.experiment, swr: arm } } }
}

/** What loadFeedWithArm needs from `fetch`. */
export type FeedFetch = (url: string) => Promise<{ ok: boolean, status: number, json (): Promise<any> }>

/**
 * loadListingsSm's wiring (Widget.tsx and Lite.tsx): the control file, then
 * this reader's arm. The reader id is read once the control file is in. An
 * arm file that answers non-ok throws, so the reader stays on control.
 */
export function loadFeedWithArm<L> (url: string, control: Promise<FeedFile<L>>, readerId: () => string, fetchFn: FeedFetch): Promise<FeedFile<L>> {
  return control.then((res) => withFeedArm<L>(url, res, readerId(), (u) => fetchFn(u).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() })))
}

/** The reader's stored id, or '' where storage is blocked (then the reader stays on control). */
export function safeReaderId (getUid: () => string): string {
  try { return getUid() } catch { return '' }
}
